import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

import { calculateReport } from '../../services/reportsApi.js';

/**
 * Verdichtet die rechenrelevanten Eingabewerte zu einem String, der sich genau dann aendert, wenn
 * der Live-Vorschau-Endpunkt (`/api/reports/calculate`) neu abgefragt werden muss (D-10).
 *
 * Achtung, Pitfall (Phase 30, 30-RESEARCH.md Frage 2/Pitfall 1): `.join('|')` ueber ein Array von
 * Objekten liefert IMMER `"[object Object]"`, unabhaengig vom Inhalt -- ein naiver `values[key] ??
 * ''`-Zugriff auf ein Array/Objekt-Feld (z.B. `primaryFuels`) erkennt eine Aenderung innerhalb des
 * Arrays deshalb nie, der Debounce-Effekt feuert nie, und eine davon abhaengige Spalte bleibt
 * stumm. Werte vom Typ `object` (Arrays und verschachtelte Objekte) gehen deshalb ueber
 * `JSON.stringify()` in den Schluessel, alle uebrigen wie bisher ueber `?? ''`.
 *
 * Dieser Hook ist geteilt ueber alle fuenf Formulare (Unfall-, Umwelt- und drei Marktformulare).
 * Die Aenderung ist additiv: sie greift nur, wenn ein Wert tatsaechlich ein Objekt ist, und aendert
 * nur, WANN gerechnet wird, nicht WAS an Payload gesendet wird -- der Payload-Aufbau in
 * `useReportCalc` selbst iteriert ohnehin ueber `calcInputKeys` und war fuer Arrays schon immer
 * richtig.
 */
export function buildCalcKey(
  calcInputKeys: readonly string[],
  values: Record<string, unknown>
): string {
  return calcInputKeys
    .map((key) => {
      const v = values[key];
      return v !== null && typeof v === 'object' ? JSON.stringify(v) : (v ?? '');
    })
    .join('|');
}

/**
 * Geteilter, debounced Live-Vorschau-Hook gegen /api/reports/calculate (D-10).
 *
 * Extrahiert die in AccidentDataForm und EnvironmentalDataForm fast wortgleich
 * doppelte calcKey/Debounce/useEffect-Verdrahtung. Der zurueckgegebene
 * `onValuesChange` wird als `opts.onValuesChange` an `useReportForm` durchgereicht.
 *
 * `initialCalc` seedet die Anzeige ohne Netzaufruf beim Oeffnen: solange der
 * debouncedCalcKey sich seit dem Laden nicht veraendert hat, werden die
 * persistierten abgeleiteten Werte des geladenen Reports angezeigt (heute nur bei
 * Unfall aktiv, hier generalisiert fuer beide).
 *
 * 18-06-Praezisierung: Anders als die alte, formularinterne Nutzung (die Form-
 * Komponente mountete erst NACHDEM `report` bereits geladen war, `initialCalc` war
 * beim allerersten Hook-Aufruf also schon echt) haengt dieser Hook jetzt bereits VOR
 * dem Report-Laden auf Seiten-Ebene (ReportEditPage rendert seine Hooks unbedingt,
 * `report` startet mit null). Ohne die beiden Faelle unten wuerde a) der
 * Mount-Effect sofort einen Calculate-Call mit leerem {}-Payload absetzen und b) die
 * Seed-Guard-Baseline (`loadedCalcKey`) dauerhaft auf diesem leeren Payload
 * einfrieren, sodass der spaeter eintreffende echte D-03-Seed-Wert IMMER als
 * "veraendert" gilt und einen weiteren, ueberfluessigen Call ausloest.
 *
 * Phase 44 (Vergleichswert Inlandsversand): `reportId` geht als Meldungskontext an den Server.
 * Der Effekt feuert nur bei einem Wechsel von `debouncedCalcKey`. Der alte Startwert war
 * `buildCalcKey(calcInputKeys, {})`; eine LEERE Meldung liefert nach dem Seeding exakt denselben
 * Schluessel, es gab also nie einen Rechenaufruf und der Vergleichswert fehlte bei leeren
 * (typisch: laufenden) Perioden. Deshalb startet `calcKey` mit dem Sentinel `UNSEEDED_CALC_KEY`,
 * der nie aus `buildCalcKey` entstehen kann: Nach dem Seeding wechselt der Schluessel immer
 * genau einmal. Der Seed-Schutz fuer `initialCalc` (Unfall/Umwelt) bleibt unveraendert.
 */
const UNSEEDED_CALC_KEY = '\u0000unseeded';

export function useReportCalc<TCalc>(
  kind: string,
  calcInputKeys: readonly string[],
  initialCalc: TCalc | null,
  reportId?: number
) {
  const valuesRef = useRef<Record<string, unknown>>({});

  const [calcKey, setCalcKey] = useState<string>(UNSEEDED_CALC_KEY);
  const [debouncedCalcKey] = useDebouncedValue(calcKey, 300);
  const loadedCalcKey = useRef(calcKey);

  // Ob der erste echte onValuesChange-Aufruf (das D-03-Seeding aus fetchReport)
  // bereits eingetroffen ist. Vor diesem Zeitpunkt gibt es keine echten Werte im
  // Formular -- ein Calculate-Call waere sinnlos (leerer Payload).
  const hasSeeded = useRef(false);

  const [calc, setCalc] = useState<TCalc | null>(initialCalc);

  // Backfill der Anzeige, sobald initialCalc eintrifft (der Report also geladen ist).
  // Der useState-Initialwert oben ist beim allerersten Render (Report noch nicht
  // geladen) zwingend null; React zieht Hook-Initialwerte nicht automatisch nach.
  useEffect(() => {
    if (initialCalc !== null && calc === null) {
      setCalc(initialCalc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCalc]);

  function onValuesChange(values: Record<string, unknown>): void {
    valuesRef.current = values;
    const key = String(reportId ?? '') + '#' + buildCalcKey(calcInputKeys, values);
    setCalcKey(key);
    if (!hasSeeded.current) {
      // Baseline nachtraeglich auf den ECHTEN Seed-Key setzen statt auf den beim
      // Mount vorab berechneten Leer-Payload-Key.
      hasSeeded.current = true;
      loadedCalcKey.current = key;
    }
  }

  useEffect(() => {
    if (!hasSeeded.current) {
      return;
    }
    if (initialCalc && debouncedCalcKey === loadedCalcKey.current) {
      return;
    }
    let cancelled = false;
    const values = valuesRef.current;
    const payload: Record<string, unknown> = {};
    for (const key of calcInputKeys) payload[key] = values[key] ?? null;
    calculateReport(kind, payload, reportId)
      .then((result) => {
        if (!cancelled) setCalc(result as TCalc);
      })
      .catch(() => {
        if (!cancelled) setCalc(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCalcKey]);

  return { calc, onValuesChange };
}
